'use client';

import React, { createContext, ReactNode, useContext, useEffect, useRef } from 'react';

import { DatabaseHealth } from '@/lib/database.service';
import { LogType, logTransaction } from '@/lib/logTransaction';
import { formatErrorMessage } from '@/lib/functions';

interface DatabaseProviderState {
  error: string | null;
  health: DatabaseHealth | null;
  initialized: boolean;
  ready: boolean;
}

interface DatabaseProviderProps {
  children: ReactNode;
}

const initialState: DatabaseProviderState = {
  error: null,
  health: null,
  initialized: false,
  ready: false,
};

export const databaseProvider = {
  state: initialState,
  actions: {} as {
    checkHealth: () => Promise<DatabaseHealth | null>;
    initDatabase: () => Promise<DatabaseHealth | null>;
  },
};

export const DatabaseContext = createContext<typeof databaseProvider>(databaseProvider);

export const useDatabase = () => useContext(DatabaseContext);

const DatabaseProvider: React.FC<DatabaseProviderProps> = ({ children }) => {
  const [state, setState] = React.useState<DatabaseProviderState>(initialState);
  const initStarted = useRef(false);

  const checkHealth = async (): Promise<DatabaseHealth | null> => {
    logTransaction(LogType.Internal, 'checkHealth called');

    try {
      const response = await fetch('/api/db/health', { credentials: 'include' });
      const json = (await response.json()) as { health: DatabaseHealth; ready: boolean };

      setState({
        error: response.ok ? null : 'Database is not ready',
        health: json.health,
        initialized: true,
        ready: json.ready,
      });

      logTransaction(LogType.Internal, 'checkHealth completed successfully', {
        ready: json.ready,
        schemaVersion: json.health.schemaVersion,
      });

      return json.health;
    } catch (error) {
      const message = formatErrorMessage(error) ?? 'Database health check failed';

      setState({
        error: message,
        health: null,
        initialized: true,
        ready: false,
      });

      logTransaction(LogType.Error, 'checkHealth failed', { error: message });
      return null;
    }
  };

  const initDatabase = async (): Promise<DatabaseHealth | null> => {
    logTransaction(LogType.Internal, 'initDatabase called');
    return checkHealth();
  };

  useEffect(() => {
    if (initStarted.current) return;
    initStarted.current = true;
    void initDatabase();
  }, []);

  return (
    <DatabaseContext.Provider
      value={{
        state,
        actions: {
          checkHealth,
          initDatabase,
        },
      }}>
      {children}
    </DatabaseContext.Provider>
  );
};

export default DatabaseProvider;
